import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { datePicker } from 'src/app/shared/mk-configs/datePicker/datePickerLocale';
import { PayrollDateRange, Options } from 'src/app/interfaces/payroll/payroll-interfaces';
import { UserService } from 'src/app/shared/services/user.service';
import * as moment from 'moment-timezone';
import { LanguageService } from 'src/app/shared/services/language.service';

@Component({
  selector: 'dynamic-calendar',
  templateUrl: './dynamic-calendar.component.html',
  styleUrls: ['./dynamic-calendar.component.scss']
})
export class DynamicCalendarComponent implements OnInit, OnChanges, OnDestroy {

  @Output() onChangeDate: EventEmitter<PayrollDateRange> = new EventEmitter<PayrollDateRange>();

  @Input() isPrepayroll: boolean = false;
  @Input() options: Options = {id: null, type: null};
  @Input() isSpecial: boolean = false;
  @Input() showLabel: boolean = false;
  @Input() dates: PayrollDateRange = { startDate: null, endDate: null };
  @Input() weekConfig: { dow: number, doy: number };

  public selectedDate: PayrollDateRange = { startDate: null, endDate: null };
  public maxDate: moment.Moment = moment().locale('es-payroll').add(20, 'w');
  public periodOfYear: number = 1;
  public typeOfPeriod: string;
  public locale: object = datePicker.locale.spanish;
  public jsonLanguage: any;
  private changeLanguage: any;
  private customLocale = 'es-user';

  constructor(
    private userService: UserService,
    public languageService: LanguageService
  ) { 
    
    this.locale = this.languageService.default_lang == "MX" ? datePicker.locale.spanish : this.languageService.default_lang == "US" ? datePicker.locale.english : datePicker.locale.spanish;
  
    this.changeLanguage = languageService.emitChangeLanguage.subscribe(async () => {
      this.jsonLanguage = await this.languageService?.getJsonLanguege('components','dynamic_calendar');
      this.locale = this.languageService.default_lang == "MX" ? datePicker.locale.spanish : this.languageService.default_lang == "US" ? datePicker.locale.english : datePicker.locale.spanish;
    })
  }
  
  ngOnChanges(changes: SimpleChanges) {
    if (changes.weekConfig) {
      const currentLocale = moment.locale();
      moment.defineLocale('es-payroll', {
        parentLocale: 'es',
        week: {
          dow: this.weekConfig?.dow ?? this.userService?.user?.startWeek,
          doy: this.weekConfig?.doy ?? this.userService?.user?.doyMoment
        }
      });
      moment.locale(currentLocale);
    }
    this.typeOfPeriod = this.options?.calendarType || this.userService?.user?.calendarType;
    if (this.options?.type == "EDIT") this.getPeriod(this.dates?.startDate);
    else this.getPeriod(moment().locale('es-payroll'));
  }

  ngOnDestroy(){
    this.changeLanguage.unsubscribe();
  }

  async ngOnInit() {
    this.jsonLanguage = await this.languageService?.getJsonLanguege('components','dynamic_calendar');

    const currentLocale = moment.locale();
    moment.defineLocale('es-payroll', {
      parentLocale: 'es',
      week: {
        dow: this.weekConfig?.dow ?? this.userService?.user?.startWeek,
        doy: this.weekConfig?.doy ?? this.userService?.user?.doyMoment
      }
    });
    moment.locale(currentLocale);
  }

  public getPeriod = (date: moment.Moment): void => {
    date.locale('es-payroll');

    let periodStart: moment.Moment;
    let periodEnd: moment.Moment;
    let periodCount: number = 1;

    switch (this.typeOfPeriod) {
      case "CATORCENAL": {

        periodStart = date.clone().startOf('week').week(1);
        periodEnd = periodStart.clone().add(13, 'days');

        while((date.isBefore(periodStart) || date.isAfter(periodEnd))){
          periodStart.add(14, 'days');
          periodEnd.add(14, 'days');
          periodCount++;
        }
        
        this.periodOfYear = periodCount;
        this.selectedDate = { startDate: periodStart, endDate: periodEnd, week: this.periodOfYear, year:  parseInt(periodStart.format("YYYY")) };

        this.onChangeDate.emit(this.selectedDate);
        break;

      }
      case "SEMANAL": {
   
        periodStart = date.clone().startOf('week');
        periodEnd = date.clone().endOf('week');
        this.periodOfYear = date.week();

        // Se valida el año de las dos fechas .
        let startYear = parseInt(periodStart.format("YYYY"));
        let endYear = parseInt(periodEnd.format("YYYY"));
        let year = startYear > endYear ? startYear : endYear;

        this.selectedDate = { startDate: periodStart, endDate: periodEnd, week: this.periodOfYear, year: year };
   
        this.onChangeDate.emit(this.selectedDate);
        break;

      }
      case "QUINCENAL": {

        let monthFifteenthDay = date.clone().date(15);
        let periodCount = date.clone().month() + 1;

        if(date.isAfter(monthFifteenthDay)){
          periodStart = date.clone().date(16);
          periodEnd = periodStart.clone().endOf('month');
          this.periodOfYear = periodCount * 2;
        }
        else {
          periodStart = date.clone().startOf('month');
          periodEnd = monthFifteenthDay.clone();
          this.periodOfYear = (periodCount * 2) - 1;
        }

        this.selectedDate = { startDate: periodStart, endDate: periodEnd, week: this.periodOfYear, year:  parseInt(periodStart.format("YYYY"))};
        this.onChangeDate.emit(this.selectedDate);

        break;

      }
      case "MENSUAL": {
        periodStart = date.clone().startOf('month');
        periodEnd = date.clone().endOf('month');
        this.periodOfYear = date.month() + 1;
        this.selectedDate = { startDate: periodStart, endDate: periodEnd, week: this.periodOfYear, year:  parseInt(periodStart.format("YYYY")) };

        this.onChangeDate.emit(this.selectedDate);
        break;
      }
      case "VARIABLE": {

        periodStart = date.clone().startOf('week').week(1);
        periodEnd = periodStart.clone().add(13, 'days');

        while((date.isBefore(periodStart) || date.isAfter(periodEnd))){
          periodStart.add(14, 'days');
          periodEnd.add(14, 'days');
          periodCount++;
        }

        this.selectedDate = { startDate: periodStart, endDate: periodEnd };
        this.periodOfYear = periodCount;        

        this.onChangeDate.emit(this.selectedDate);

        break;

      }
      default: {
        break;
      }
    }

  }

  selectDate(event: any) {
    if (event.startDate) {
      this.getPeriod(event.startDate);
    }
  }

  public validYear(startDate,endDate){
    // Se valida el año de las dos fechas .
    let startYear = parseInt(startDate.format("YYYY"));
    let endYear = parseInt(endDate.format("YYYY"));
    let year = startYear > endYear ? startYear : endYear;
    return year;
}

}